/**
 * Typed lookup over the Fluent catalog (spec §4.1, as amended for Fluent): a
 * message id `en.ftl` lacks, or a variable its English reads and the call
 * omits, fails `tsc` through `messages.generated.ts`.
 *
 * Plurals and selects are Fluent's own syntax, resolved by `@fluent/bundle`
 * against `Intl.PluralRules`, so a locale's grammar lives in its catalog
 * rather than in code here.
 */
import { FluentBundle, FluentNumber, FluentResource, type FluentVariable } from '@fluent/bundle';

import { EN_FTL, type MessageVariables } from './messages.generated.ts';
import { getLocale, type Locale, type ShippedLocale } from './locale.ts';
import { accent, bracket } from './pseudo.ts';

export { getLocale, setLocale, type Locale } from './locale.ts';

export type Key = keyof MessageVariables;

type Vars<K extends Key> = MessageVariables[K];

export type ParamsOf<K extends Key> = [Vars<K>] extends [never] ? never : keyof Vars<K>;

/** A parameter may itself be a message, translated in the same locale. */
export type Param = string | number | Message;

export type ParamRecord<K extends Key> = { [P in keyof Vars<K>]: Vars<K>[P] extends 'number' ? number : Param };

type MessageFor<K extends Key> = [Vars<K>] extends [never]
  ? { key: K; params?: undefined }
  : { key: K; params: ParamRecord<K> };

/**
 * What a pure module returns instead of a sentence (spec §4.2), so the
 * module stays testable and the `.tsx` decides the language.
 */
export type Message = { [K in Key]: MessageFor<K> }[Key];

/** A message that reads no variable, so a table of them can be passed to `t` as they are. */
export type PlainKey = { [K in Key]: [Vars<K>] extends [never] ? K : never }[Key];

type ArgsFor<K extends Key> = [Vars<K>] extends [never] ? [] : [params: ParamRecord<K>];

type Params = Readonly<Record<string, Param>>;

/**
 * An English number prints as `String(n)`, as the template literals did:
 * Fluent's default `Intl.NumberFormat` would group "1234" as "1,234", a copy
 * change this phase does not make. Still a `FluentNumber`, so a plural
 * selector matches on it.
 */
class PlainNumber extends FluentNumber {
  override toString(): string {
    return String(this.value);
  }
}

/**
 * Isolation marks off for an English page: its output stays byte-identical to
 * the literals it replaced, and an all-LTR page has nothing to isolate. On
 * for every other locale, the English fallback behind a catalog included,
 * because an English word inside an Arabic sentence is exactly what FSI/PDI
 * are for.
 */
function englishBundle(useIsolating: boolean, transform?: (text: string) => string): FluentBundle {
  const bundle = new FluentBundle('en', { useIsolating, transform });
  const errors = bundle.addResource(new FluentResource(EN_FTL));
  if (errors.length > 0) throw errors[0];
  return bundle;
}

const bundles = new Map<Locale, readonly FluentBundle[]>();

function bundlesFor(locale: Locale): readonly FluentBundle[] {
  let chain = bundles.get(locale);
  if (!chain) {
    // A locale whose catalog has not loaded is English until `addCatalog`
    // replaces this chain.
    chain = [locale === 'qps' ? englishBundle(false, accent) : englishBundle(locale !== 'en')];
    bundles.set(locale, chain);
  }
  return chain;
}

/**
 * Puts a fetched catalog in front of English for its locale, so a message
 * the catalog lacks falls back to English one message at a time (spec §4.3).
 * Returns what Fluent could not take; the rest of the catalog is used.
 */
export function addCatalog(locale: Exclude<ShippedLocale, 'en'>, source: string): Error[] {
  const bundle = new FluentBundle(locale, { useIsolating: true });
  const errors = bundle.addResource(new FluentResource(source));
  bundles.set(locale, [bundle, englishBundle(true)]);
  return errors;
}

export function hasCatalog(locale: ShippedLocale): boolean {
  return locale === 'en' || (bundles.get(locale)?.length ?? 0) > 1;
}

/**
 * The first bundle in the chain that has the message formats it, so a locale
 * missing a message shows the English. A missing variable is left visible as
 * `{$name}` by Fluent itself: never a blank.
 */
export function formatIn(chain: readonly FluentBundle[], key: string, args?: Record<string, FluentVariable>): string {
  for (const bundle of chain) {
    const message = bundle.getMessage(key);
    if (message?.value) {
      // Fluent formats what it can and reports the rest here; left unread, a
      // broken translation would degrade with nothing anywhere saying so.
      const errors: Error[] = [];
      const text = bundle.formatPattern(message.value, args, errors);
      if (errors.length > 0) console.warn(`i18n: "${key}" in ${bundle.locales[0]}:`, ...errors);
      return text;
    }
  }
  return key;
}

function toArgs(locale: Locale, params: Params | undefined): Record<string, FluentVariable> | undefined {
  if (!params) return undefined;
  const english = locale === 'en' || locale === 'qps';
  return Object.fromEntries(
    Object.entries(params).map(([name, value]) => {
      // Elsewhere a bare number, which Fluent formats with the bundle's own
      // `Intl.NumberFormat`.
      if (typeof value === 'number') return [name, english ? new PlainNumber(value) : value];
      if (typeof value === 'string') return [name, value];
      return [name, translate(locale, value.key, value.params)];
    }),
  );
}

export function translate(locale: Locale, key: Key, params?: Params): string {
  const text = formatIn(bundlesFor(locale), key, toArgs(locale, params));
  return locale === 'qps' ? bracket(text) : text;
}

export function t<K extends Key>(key: K, ...params: ArgsFor<K>): string;
export function t(key: PlainKey): string;
export function t(message: Message): string;
export function t(keyOrMessage: Key | Message, params?: Params): string {
  const m = typeof keyOrMessage === 'string' ? { key: keyOrMessage, params } : keyOrMessage;
  return translate(getLocale(), m.key, m.params);
}

/** Builds a `Message` with `t`'s own typing, so a pure module cannot make one `t` would refuse. */
export function msg<K extends Key>(key: K, ...params: ArgsFor<K>): Message {
  return (params[0] === undefined ? { key } : { key, params: params[0] }) as Message;
}
